/**
 * timeline: when an agent actually worked, not just when it first started.
 *
 * A subagent or workflow agent can work in bursts over hours: it ends its turn, then a
 * message (SendMessage, a coordinator) wakes it again; its context can be compacted or
 * reset on the way. The machine agent folds the agent's own transcript into `Activity`
 * (incrementally, by byte offset) and the timeline draws one block per burst with the idle
 * gaps between them, and a mark where it was resumed, compacted or reset.
 */
import type { Ts } from '../index.ts';

/** One stretch of work: from the first to the last transcript entry of a burst. */
export interface ActivitySpan {
  from: Ts;
  to: Ts;
}

/**
 * resume: new input after the agent had ended its turn or gone quiet (a SendMessage
 *   continuation, a coordinator's message, a person's follow-up);
 * compact: the conversation was compacted ("continued from a previous conversation");
 * reset: the conversation restarted with no summary (a cleared context).
 */
export type ActivityMarkKind = 'resume' | 'compact' | 'reset';

export interface ActivityMark {
  at: Ts;
  kind: ActivityMarkKind;
  /** Who or what caused it, in a few words ("message from the coordinator", "automatic"). */
  detail?: string;
}

/** An agent's activity, from its transcript. */
export interface Activity {
  /** Bursts of work, oldest first, at most ACTIVITY_SPANS_MAX (the closest ones merged beyond it). */
  spans: ActivitySpan[];
  /** Resumes, compactions and resets, oldest first, the newest ACTIVITY_MARKS_MAX. */
  marks: ActivityMark[];
  /** Sum of the spans. */
  activeMs: number;
  /** The last entry's time. */
  lastAt: Ts;
  /**
   * Mid-turn at lastAt: a tool call or a model reply is outstanding, so the agent is at work
   * past lastAt. False once it ended its turn.
   */
  busy: boolean;
  /** The spans do not cover the whole transcript yet (it is still being read, a few MB a pass). */
  partial?: boolean;
}

/** A gap longer than this between entries, after the agent ended its turn or before new input, is idle time. */
export const ACTIVITY_IDLE_GAP_MS = 2 * 60_000;
export const ACTIVITY_SPANS_MAX = 64;
export const ACTIVITY_MARKS_MAX = 32;
/** Activity is computed for agents that worked within this long (the timeline's widest window). */
export const ACTIVITY_LOOKBACK_MS = 24 * 60 * 60_000;

/** An agent marked running with no transcript entry for this long, and not mid-tool, looks stuck. */
export const AGENT_STUCK_MS = 10 * 60_000;
/** One tool call or reply outstanding for this long looks stuck too. */
export const AGENT_STUCK_BUSY_MS = 30 * 60_000;

export const ACTIVITY_MARK_LABEL: Record<ActivityMarkKind, string> = {
  resume: 'Resumed',
  compact: 'Context compacted',
  reset: 'Context reset',
};

/** Time active in [from, to] (spans clipped to it). */
export function activeWithin(a: Pick<Activity, 'spans'>, from: Ts, to: Ts): number {
  let ms = 0;
  for (const s of a.spans) ms += Math.max(0, Math.min(s.to, to) - Math.max(s.from, from));
  return ms;
}
